import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three-stdlib';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Phoneme } from '../../types/phoneme';
import type { AvatarState } from '../../types/voice';
import AvatarStateIndicator from './AvatarStateIndicator';
import AvatarControls from './AvatarControls';
import type { utteranceData } from '../../utils/ttsService';
import { Mic, MicOff, PhoneCallIcon, PhoneOff } from 'lucide-react';
import useAgentCall, { type TranscriptItem } from '../../hooks/useAgentCall';
import { Lipsync } from 'wawa-lipsync';


interface AvatarDisplayProps {
  phonemes: Phoneme[];
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  speechSynthesisData?: utteranceData;
  state: AvatarState;
  lipsync?: Lipsync;
  sessionId?: string;
  avatarId?: string | null;
  onStateChange?: (state: AvatarState) => void;
  onCallEnd?: (transcript: TranscriptItem[]) => void;
}

export default function AvatarDisplay({ phonemes, state, onStateChange, speechSynthesisData, onCallEnd, lipsync: propsLipsync, sessionId, avatarId }: AvatarDisplayProps) {

  const containerRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const avatarRef = useRef<THREE.Object3D | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const lipsMesh = useRef<THREE.Mesh | null>(null);
  const mouthMesh = useRef<THREE.Mesh | null>(null);
  const toothMesh = useRef<THREE.Mesh | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const animationsMapRef = useRef<Map<string, THREE.AnimationAction>>(new Map());
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const clockRef = useRef<THREE.Clock>(new THREE.Clock());
  const animationFrameRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  const avatarAudio = useRef<HTMLAudioElement | null>(null)
  const {
    isDialing,
    isCallActive,
    isMuted,
    isSpeaking,
    startCall,
    endCall,
    toggleMute,
    lipsync: callLipsync
  } = useAgentCall(avatarAudio, onCallEnd, sessionId, avatarId);

  // Use the shared lipsync instance from props (chat) or the one from call
  // Priorizar callLipsync si hay una llamada activa para que use el analizador correcto
  const lipsync = isCallActive ? callLipsync : (propsLipsync || callLipsync);

  // const { lipsync } = useAvatarAudio(); // Se elimina la instancia duplicada
  // En tu componente, antes de inicializar nada:

  // Al configurar el Lipsync, pásale el contexto compartido si la librería lo permite
  // O asegúrate de que wawa-lipsync use el mismo context:


  // Mapeo unificado para los 15 visemas de wawa-lipsync -> Tus nombres de Blender
  const lipsyncMapping: Record<string, string | null> = {
    "viseme_sil": null,
    "viseme_pp": "M/P/B",
    "viseme_ff": "F/V",
    "viseme_th": "E/L/D/T/N",
    "viseme_dd": "E/L/D/T/N",
    "viseme_kk": "E/L/D/T/N",
    "viseme_ch": "E/L/D/T/N",
    "viseme_ss": "E/L/D/T/N",
    "viseme_nn": "E/L/D/T/N",
    "viseme_rr": "E/L/D/T/N",
    "viseme_aa": "A",
    "viseme_e": "E/L/D/T/N",
    "viseme_i": "I",
    "viseme_o": "O",
    "viseme_u": "U"
  };

  const toothMapping: Record<string, string | null> = {
    "A": "A",
    "O": "O",
    "U": "O",          // La mandíbula para la U es similar a la O
    "I": "I",
    "E/L/D/T/N": "E",  // Usamos tu forma E para este grupo
    "M/P/B": null,     // Boca cerrada, dientes en reposo
    "F/V": "E",        // Un poco de apertura para dejar ver los dientes
  };
  const myShapes = ["A", "E/L/D/T/N", "M/P/B", "I", "O", "U", "F/V"];

  const phonemeMap: Record<string, string> = {
    "A": "A",
    "R": "A",
    "E": "E/L/D/T/N",
    "B": "M/P/B",
    "D": "E/L/D/T/N",
    "L": "E/L/D/T/N",
    "I": "I",
    "O": "O",
    "U": "U",
    "M": "M/P/B",
    "N": "E/L/D/T/N",
    "F": "F/V",
    "V": "F/V",
    "P": "M/P/B"
  };


  /*const setPhoneme = (phoneme: string, strength: number) => {

    console.log(phoneme)
    if (lipsMesh.current && mouthMesh.current && toothMesh.current) {
      lipsMesh.current.morphTargetInfluences?.fill(0);
      toothMesh.current.morphTargetInfluences?.fill(0);
      mouthMesh.current.morphTargetInfluences?.fill(0);
      const index = lipsMesh.current.morphTargetDictionary?.[phonemeMap[phoneme]];
      if (index !== undefined) {
        lipsMesh.current.morphTargetInfluences![index] = strength; // Activa solo el actual
        if (["A", "E", "I", "O", "U"].includes(phoneme)) {
          const toothIndex = toothMesh.current.morphTargetDictionary?.[phoneme];
          const mouthIndex = mouthMesh.current.morphTargetDictionary?.[phoneme];

          if (toothIndex !== undefined && mouthIndex !== undefined) {
            mouthMesh.current.morphTargetInfluences![mouthIndex] = strength;
            toothMesh.current.morphTargetInfluences![mouthIndex] = strength;
          }
        }
      }
    }
  }*/
  useEffect(() => {
    console.log("SPEAKING")
    if (isSpeaking && isCallActive) {
      onStateChange?.("speaking");
    } else if (isCallActive) {
      onStateChange?.("listening");
    } else {
      onStateChange?.("idle")
    }

  }, [isSpeaking, isCallActive])
  /*useEffect(() => {

    phonemas.forEach(
      p => setTimeout(() => setPhoneme(p.phoneme, 1), 65)
    )

  }, [phonemas])*/
  /*useEffect(() => {

    if (speechSynthesisData?.character)
      setPhoneme(speechSynthesisData?.character.toUpperCase(), 1);



  }, [speechSynthesisData])*/
  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    const initializeScene = () => {
      if (isInitializedRef.current || !mountRef.current) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      // Skip if dimensions are zero
      if (width === 0 || height === 0) return;

      if (!sceneRef.current) {
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
          75,
          width / height,
          0.1,
          1000
        );
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setClearColor(0x000000, 0);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        mountRef.current.appendChild(renderer.domElement);

        // Set background based on state
        //scene.background = new THREE.Color(0xf3f4f6);

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(0, 1, 2);
        scene.add(directionalLight);

        const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
        backLight.position.set(0, 1, -2);
        scene.add(backLight);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = true;
        controls.enablePan = true;
        controls.minDistance = 0.2;
        controls.maxDistance = 1;

        sceneRef.current = scene;
        cameraRef.current = camera;
        rendererRef.current = renderer;
        controlsRef.current = controls;

        camera.position.z = 0.6;
        camera.position.y = 0.15;

        // Load avatar model
        const loader = new GLTFLoader();
        loader.load("/3dmodels/ai-avatar_2.glb", (gltf) => {
          avatarRef.current = gltf.scene;
          scene.add(gltf.scene);

          // Get morph targets
          lipsMesh.current = avatarRef.current.getObjectByName('defaultMaterial026') as THREE.Mesh;
          mouthMesh.current = avatarRef.current.getObjectByName('defaultMaterial021_1') as THREE.Mesh;
          toothMesh.current = avatarRef.current.getObjectByName('defaultMaterial021_2') as THREE.Mesh;

          // Initialize animation mixer
          if (avatarRef.current) {
            mixerRef.current = new THREE.AnimationMixer(avatarRef.current);
            // Load animations from GLTF if present
            if ((gltf as any).animations && (gltf as any).animations.length > 0) {
              (gltf as any).animations.forEach((clip: THREE.AnimationClip) => {
                const action = mixerRef.current!.clipAction(clip);
                animationsMapRef.current.set(clip.name, action);
              });
            }
            // Play default animation named 'Animation'
            const defaultAnim = animationsMapRef.current.get('Animation');
            if (defaultAnim) defaultAnim.play();
          }
        });

        isInitializedRef.current = true;
      }
    };

    // Initialize immediately if dimensions are valid
    initializeScene();

    // Also initialize when dimensions become available
    const resizeObserver = new ResizeObserver(() => {
      initializeScene();
    });

    resizeObserver.observe(mountRef.current);

    // Also listen to fullscreen changes as they may affect layout
    /*const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Use requestAnimationFrame to ensure DOM has updated after fullscreen change
      requestAnimationFrame(initializeScene);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };*/
  }, []);

  // Animation loop
  const animate = useCallback(() => {

    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
    let delta = clockRef.current.getDelta();
    delta = Math.min(delta, 0.1);


    // Update state based on audio if audioRef is provided
    /*if (avatarAudio?.current) {
       if (avatarAudio.current.paused && state === 'speaking') {
         onStateChange?.('idle');
       } else if (!avatarAudio.current.paused && state !== 'speaking') {
         //onStateChange?.('speaking');
       }
 
       // Lip sync
       if (!avatarAudio.current.paused && avatarRef.current) {
         console.log("speaking")
         const t = avatarAudio.current.currentTime;
 
         if (lipsMesh.current && mouthMesh.current && toothMesh.current) {
           // Reset morph targets
           lipsMesh.current.morphTargetInfluences?.fill(0);
           toothMesh.current.morphTargetInfluences?.fill(0);
           mouthMesh.current.morphTargetInfluences?.fill(0);
 
           // Apply phonemes
           phonemes.forEach((p) => {
             if (t >= p.start && t <= p.end) {
               const dict = lipsMesh.current?.morphTargetDictionary;
               const dict2 = toothMesh.current?.morphTargetDictionary;
 
               if (dict && dict2) {
                 const idx = dict[phonemeMap[p.phoneme.toUpperCase()]];
                 const idx2 = dict2[p.phoneme.toUpperCase()];
 
                 if (idx !== undefined && lipsMesh.current) {
                   lipsMesh.current.morphTargetInfluences![idx] = 1.0;
                 }
 
                 if (idx2 !== undefined && mouthMesh.current && toothMesh.current) {
                   mouthMesh.current.morphTargetInfluences![idx2] = 1.0;
                   toothMesh.current.morphTargetInfluences![idx2] = 1.0;
                 }
               }
             }
           });
         }
       }
     }*/


    // --- LÓGICA DE WAWA-LIPSYNC ---
    let targetShape: string | null = null;

    // Get frequency data if available (for manual analysis)
    let frequencyData: Uint8Array | null = null;
    if ((lipsync as any)?.analyser) {
      frequencyData = new Uint8Array((lipsync as any).analyser.frequencyBinCount);
      (lipsync as any).analyser.getByteFrequencyData(frequencyData);
    }

    // Calculate audio energy
    const avgEnergy = frequencyData ? Array.from(frequencyData).reduce((a, b) => a + b, 0) / frequencyData.length : 0;

    // Try to process audio
    lipsync?.processAudio();

    let currentViseme = lipsync?.viseme;

    // Debug: Log viseme every 30 frames
    if (lipsync && Math.random() < 0.033) {
      console.log("Current Context Time:", (lipsync as any).audioContext.currentTime);
      const hasAnalyser = !!(lipsync as any).analyser;
      const freqDataAvailable = frequencyData && frequencyData.length > 0;
      console.log(`[Lipsync] Viseme: ${currentViseme}, Has Analyser: ${hasAnalyser}, Freq Data: ${freqDataAvailable}, Energy: ${avgEnergy.toFixed(2)}`);
    }

    // IMPORTANT: Only use viseme if there's significant audio energy
    // If energy is too low, force silence (null = closed mouth)
    if (avgEnergy < 3) {
      targetShape = null; // Silence - close mouth
    } else if (currentViseme) {
      // Use the lipsync viseme mapping
      targetShape = lipsyncMapping[currentViseme.toLowerCase()];
    }

    // Fallback: If still in speaking state, use TTS phoneme
    if (!targetShape && state === 'speaking' && speechSynthesisData?.character) {
      const char = speechSynthesisData.character.toUpperCase();
      targetShape = phonemeMap[char] || "E/L/D/T/N";
    }

    // Calculamos una intensidad basada en el volumen para que la boca no sea estática
    // Mapeamos la energía (0-100) a un rango de apertura (0.4 - 1.1)
    const intensity = targetShape ? 1 : 0;

    const lerpSpeed = 0.5 //delta * 30; // Un poco más rápido para mayor fluidez

    // 1. Procesar LABIOS (lipsMesh)
    myShapes.forEach((shapeName) => {
      const idx = lipsMesh.current?.morphTargetDictionary?.[shapeName];
      if (idx !== undefined) {
        // Usamos la intensidad dinámica en lugar de un 1.0 estático
        const targetValue = (shapeName === targetShape) ? intensity : 0.0;
        lipsMesh.current!.morphTargetInfluences![idx] = THREE.MathUtils.lerp(
          lipsMesh.current!.morphTargetInfluences![idx],
          targetValue,
          lerpSpeed
        );
      }
    });

    // 2. Procesar DIENTES y BOCA INTERNA (toothMesh y mouthMesh)
    const targetToothShape = targetShape ? toothMapping[targetShape] : null;

    ["O", "E", "I", "A"].forEach((toothShape) => {
      const idxT = toothMesh.current?.morphTargetDictionary?.[toothShape];
      const idxM = mouthMesh.current?.morphTargetDictionary?.[toothShape];

      // El targetValue aquí será 0.0 si targetToothShape es null, 
      // forzando a los dientes a cerrarse.
      const targetValue = (toothShape === targetToothShape) ? intensity : 0.0;

      if (idxT !== undefined) {
        toothMesh.current!.morphTargetInfluences![idxT] = THREE.MathUtils.lerp(
          toothMesh.current!.morphTargetInfluences![idxT],
          targetValue,
          lerpSpeed
        );
      }
      if (idxM !== undefined) {
        mouthMesh.current!.morphTargetInfluences![idxM] = THREE.MathUtils.lerp(
          mouthMesh.current!.morphTargetInfluences![idxM],
          targetValue,
          lerpSpeed
        );
      }
    });
    // Auto-rotate
    if (isRotating && avatarRef.current) {
      avatarRef.current.rotation.y += 0.005;
    }

    // Update animations

    mixerRef.current?.update(delta);

    controlsRef.current?.update();
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [lipsync, avatarAudio, phonemes, state, isRotating, speechSynthesisData]);



  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [animate]);

  // Handle resize with ResizeObserver
  useEffect(() => {
    if (!mountRef.current) return;

    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      // Skip if dimensions are zero
      if (width === 0 || height === 0) return;

      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };

    // Use ResizeObserver to watch the container element
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(mountRef.current);

    // Also listen to fullscreen changes as they may affect layout
    const handleFullscreenChange = () => {

      const isNativeFullscreen = !!document.fullscreenElement;
      console.log(`Full screen action nativo:${isNativeFullscreen} , esIos: ${isIOS}`)
      // Only update if we're in native fullscreen mode
      if (!isIOS) {
        setIsFullscreen(isNativeFullscreen);
      }
      // Use requestAnimationFrame to ensure DOM has updated after fullscreen change
      requestAnimationFrame(handleResize);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Initialize scene when container dimensions become available
  useEffect(() => {
    if (!mountRef.current) return;

    // If scene already initialized, nothing to do
    if (sceneRef.current) return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    // If dimensions are valid, initialize now
    if (width > 0 && height > 0) {
      // This will trigger the initialization effect
      // We use a dummy state update or just let the effect run
      // Actually we need to call the initialization logic
      // But the initialization is in a separate useEffect that already runs
      // So we need to ensure it runs again. We can use a ref to track initialization
      // Actually simpler: we can move the initialization into a function and call it here
      // But for now, the initialization effect will run on mount. If dimensions are zero, it returns early.
      // We need to retry when dimensions become available.

      // Let's create a retry mechanism using a flag
      const tryInitialize = () => {
        if (sceneRef.current) return;

        const currentWidth = mountRef.current?.clientWidth;
        const currentHeight = mountRef.current?.clientHeight;

        if (currentWidth && currentHeight && currentWidth > 0 && currentHeight > 0) {
          // Trigger re-render to make the initialization effect run again
          // But we can't force a re-render easily without state
          // Instead, we can directly initialize here by calling the same code
          // But that would duplicate code. Better approach: use a ref as a trigger

          // Actually, the simplest fix: we already have a ResizeObserver that calls handleResize
          // But handleResize only updates camera/renderer if they exist. It doesn't create them.

          // We need to ensure the initialization effect runs when dimensions are ready.
          // We can use a state variable to track "isReady" but that would cause re-renders.

          // Alternative: the initialization effect should be more intelligent.
          // Let's modify the initialization effect to also check periodically or on resize if it should initialize.
        }
      };

      tryInitialize();
    }
  }, []);

  // Detect iOS
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    setIsIOS(isIOSDevice);
  }, []);

  // Control handlers
  const handleResetView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(0, 0.15, 0.6);
      cameraRef.current.lookAt(0, 0, 0);
    }
    if (avatarRef.current) {
      avatarRef.current.rotation.set(0, 0, 0);
    }
  };

  const handleZoomIn = () => {
    if (cameraRef.current && controlsRef.current) {
      const direction = new THREE.Vector3();
      cameraRef.current.getWorldDirection(direction);
      cameraRef.current.position.add(direction.multiplyScalar(0.05));
    }
  };

  const handleZoomOut = () => {
    if (cameraRef.current && controlsRef.current) {
      const direction = new THREE.Vector3();
      cameraRef.current.getWorldDirection(direction);
      cameraRef.current.position.sub(direction.multiplyScalar(0.05));
    }
  };

  const handleToggleFullscreen = async () => {
    const container = containerRef.current;
    console.log(`handelToggleFullScreen isOIS:${isIOS} fullscreenEnabled:${document.fullscreenEnabled} fullScreenElement:${document.fullscreenElement}`)
    if (!container) return;

    try {

      // Check for native Fullscreen API support (Desktop/Android)
      if (!isIOS && !document.fullscreenEnabled) {
        if (!document.fullscreenElement) {
          if (container.requestFullscreen) {
            await container.requestFullscreen().catch(err => {
              console.warn('Fullscreen API failed, falling back to CSS method:', err);
              // Fallback to CSS method
              toggleCSSFullscreen();
            });
          } else if ((containerRef.current as any).webkitRequestFullscreen) {
            // Soporte para navegadores basados en WebKit antiguo/móviles
            await (containerRef.current as any).webkitRequestFullscreen();
          }

        } else {

          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            await (document as any).webkitExitFullscreen();
          }
        }
      } else {
        // iOS or fallback: Use CSS-based fullscreen
        toggleCSSFullscreen();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const enterCSSFullscreen = () => {
    if (!containerRef.current) return;

    // Store original styles
    const container = containerRef.current;
    const originalStyles = {
      position: container.style.position,
      top: container.style.top,
      left: container.style.left,
      width: container.style.width,
      height: container.style.height,
      zIndex: container.style.zIndex
    };

    // Apply fullscreen styles
    Object.assign(container.style, {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 9999
    });

    // Store original styles for restoration
    (container as any)._originalStyles = originalStyles;
  };

  const exitCSSFullscreen = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    // Restore original styles
    if ((container as any)._originalStyles) {
      Object.assign(container.style, (container as any)._originalStyles);
      delete (container as any)._originalStyles;
    }
  };

  const toggleCSSFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (isFullscreen) {
      exitCSSFullscreen();
    } else {
      enterCSSFullscreen();
    }
  };

  const handleToggleRotate = () => {
    setIsRotating(!isRotating);
  };

  // State-based styling
  const getStateBorderColor = () => {
    switch (state) {
      case 'listening': return 'border-primary';
      case 'thinking': return 'border-secondary';
      case 'speaking': return 'border-accent';
      case 'error': return 'border-error';
      default: return 'border-transparent';
    }
  };

  return (
    <div
      ref={containerRef}
      className={`
        relative w-full transition-all duration-300 ease-in-out
        ${isFullscreen
          ? 'fixed inset-0 z-[9999] bg-base-100 w-screen h-[100dvh]'
          : 'h-screen min-h-100 rounded-2xl border-4 overflow-hidden'} 
        ${getStateBorderColor()}
      `}>
      <audio ref={avatarAudio} autoPlay style={{ display: 'none' }} />
      {/* State Indicator */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-base-100/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-md">
          <AvatarStateIndicator state={state} />
        </div>
      </div>

      {/* 3D Canvas */}
      <div
        ref={mountRef}
        className="w-full h-full bg-linear-to-b from-base-200 to-base-300 bg-no-repeat bg-center"
        style={{
          backgroundImage: "url('/bg_col50.png')",
          backgroundSize: 'cover', // Preserves the original image dimensions without resizing or distortion
          backgroundAttachment: 'fixed'
        }}
      />

      {/* Controls - positioned at top right, vertical layout */}
      <div className="absolute top-4 right-4 z-20">
        <AvatarControls
          onResetView={handleResetView}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onToggleFullscreen={handleToggleFullscreen}
          onToggleRotate={handleToggleRotate}
          isRotating={isRotating}
          isFullscreen={isFullscreen}
        />
        {isFullscreen && (
          <div className="fixed bottom-4 right-4 flex flex-col gap-2">
            {isCallActive ? (
              <>
                <button
                  className={`btn btn-circle ${isMuted ? 'btn-error' : 'bg-base-100/80'} backdrop-blur-sm shadow-lg`}
                  onClick={() => toggleMute()}
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5" />}
                </button>
                <button
                  className="btn btn-circle btn-error shadow-lg"
                  onClick={() => endCall()}
                  title="End Call"
                >
                  <PhoneOff className="w-5 h-5 text-white" />
                </button>
              </>
            ) : (
              <button
                className={`btn btn-circle bg-primary text-primary-content shadow-lg hover:scale-110 transition-transform ${isDialing ? "hidden" : ""}`}
                onClick={() => startCall()}
                title="Start Call"
              >
                <PhoneCallIcon className="w-6 h-6" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}